import * as DialogPrimitive from '@radix-ui/react-dialog'
import type { ComponentProps, ReactNode } from 'react'
import { cn } from '@/lib/cn'

export const Dialog = DialogPrimitive.Root
export const DialogTrigger = DialogPrimitive.Trigger
export const DialogClose = DialogPrimitive.Close

export interface DialogContentProps extends ComponentProps<typeof DialogPrimitive.Content> {
  /** Rendered visually hidden for screen readers. */
  description?: string
}

export function DialogContent({
  className,
  children,
  description,
  ...props
}: DialogContentProps): ReactNode {
  return (
    <DialogPrimitive.Portal>
      <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-scrim" />
      <DialogPrimitive.Content
        // Radix logs a warning when a dialog has neither a description nor an
        // explicit opt-out, and it only counts the prop as opted out when the
        // key is present, so it cannot be folded into a `?:` on the value.
        {...(description ? {} : { 'aria-describedby': undefined })}
        className={cn(
          'fixed top-1/2 left-1/2 z-50 max-h-[80vh] w-[calc(100%-2rem)] max-w-[520px] scrollbar-thin',
          '-translate-x-1/2 -translate-y-1/2 overflow-y-auto overscroll-contain',
          'rounded-panel border border-border bg-surface-raised text-fg',
          className,
        )}
        {...props}
      >
        {description ? (
          <DialogPrimitive.Description className="sr-only">
            {description}
          </DialogPrimitive.Description>
        ) : null}
        {children}
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  )
}

/**
 * Sticky rather than fixed outside the scroll area: one scroll container keeps
 * the rounded corners clipping the content without a nested overflow box.
 */
export function DialogHeader({ className, ...props }: ComponentProps<'div'>): ReactNode {
  return (
    <div
      className={cn(
        'sticky top-0 z-10 flex items-center justify-between gap-3 border-b border-border bg-surface-raised px-4 py-2.5',
        className,
      )}
      {...props}
    />
  )
}

export function DialogTitle({
  className,
  ...props
}: ComponentProps<typeof DialogPrimitive.Title>): ReactNode {
  return (
    <DialogPrimitive.Title
      className={cn('text-[12px] font-medium text-fg', className)}
      {...props}
    />
  )
}
